import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle, XCircle, RotateCcw, Search } from 'lucide-react';
import api from './api';

// Problem class definition
class Problem {
  constructor(config) {
    this.id = config.problemId;
    this.title = config.title;
    this.description = config.description;
    this.testCases = config.testCases;
    this.codeTemplate = config.codeTemplate;
    this.blanks = config.blanks;
    this.availableTokens = config.availableTokens;
    this.tags = config.tags || [];
  }

  executeCode(codeString, testCases) {
    const results = [];

    try {
      // Extract all function names from the code
      const funcMatches = Array.from(codeString.matchAll(/function\s+(\w+)/g));
      // Get the last function name, or default to 'solution'
      const funcName = funcMatches.length > 0
        ? funcMatches[funcMatches.length - 1][1]
        : 'solution';

      // Wrap code to return the function
      const wrappedCode = `(function() { ${codeString} return ${funcName}; })()`;

      // Evaluate the code to get the function
      const func = eval(wrappedCode);

      for (const testCase of testCases) {
        try {
          // Execute with timeout protection
          const startTime = Date.now();

          // Deep clone the input to prevent in-place modifications
          const input = Array.isArray(testCase.input)
            ? testCase.input.map(item => JSON.parse(JSON.stringify(item)))
            : [JSON.parse(JSON.stringify(testCase.input))];
          const result = func(...input);

          const executionTime = Date.now() - startTime;

          // Check for timeout (1 second)
          if (executionTime > 1000) {
            results.push({
              passed: false,
              error: 'Timeout: execution took too long',
              input: testCase.input,
              expected: testCase.output
            });
            continue;
          }

          // Deep equality check for arrays and objects
          const passed = JSON.stringify(result) === JSON.stringify(testCase.output);

          results.push({
            passed,
            result,
            expected: testCase.output,
            input: testCase.input
          });

        } catch (error) {
          results.push({
            passed: false,
            error: error.message,
            input: testCase.input,
            expected: testCase.output
          });
        }
      }

      return {
        allPassed: results.every(r => r.passed),
        results
      };

    } catch (error) {
      return {
        allPassed: false,
        results: [{
          passed: false,
          error: 'Compilation error: ' + error.message
        }]
      };
    }
  }

  checkSolution(userSolution) {
    // Build the complete code string
    let codeString = '';

    this.codeTemplate.forEach((line, idx) => {
      const blank = this.getBlankAtLine(idx);

      if (blank) {
        const blankIndex = this.blanks.indexOf(blank);
        const tokens = userSolution[blankIndex];
        const tokenString = tokens.map(t => t.label).join(' ');
        const completedLine = line.replace(`BLANK_${blankIndex}`, tokenString);
        codeString += completedLine + '\n';
      } else {
        codeString += line + '\n';
      }
    });

    // Debug: Log the generated code
    console.log('=== Executing Code ===');
    console.log(codeString);
    console.log('=== Test Cases ===');
    console.log(this.testCases);
    console.log('===================');

    // Execute the code against test cases
    return this.executeCode(codeString, this.testCases);
  }

  getBlankAtLine(lineIndex) {
    return this.blanks.find(b => b.lineIndex === lineIndex);
  }
}

const CodePuzzleGame = ({ selectedCategory, targetProblemId, onBackToMenu, user }) => {
  const [currentProblem, setCurrentProblem] = useState(null);
  const [selectedTokens, setSelectedTokens] = useState([]);
  const [isCorrect, setIsCorrect] = useState(null);
  const [testResults, setTestResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeBlankIndex, setActiveBlankIndex] = useState(0);
  const [insertionPosition, setInsertionPosition] = useState(null); // null means append to end
  const [categoryProblems, setCategoryProblems] = useState([]);
  const [currentProblemIndex, setCurrentProblemIndex] = useState(0);
  const [showCongratsModal, setShowCongratsModal] = useState(false);
  const [solvedInSession, setSolvedInSession] = useState(new Set()); // Track problems solved in this session
  const testResultsRef = useRef(null); // Ref for auto-scrolling to test results
  const feedbackRef = useRef(null); // Ref for auto-scrolling to feedback
  const [showTagEditor, setShowTagEditor] = useState(false);
  const [editingTags, setEditingTags] = useState('');
  const lastTapRef = useRef(0); // For double-tap detection
  const [showJumpDialog, setShowJumpDialog] = useState(false);
  const [jumpSearchQuery, setJumpSearchQuery] = useState('');
  const [allProblems, setAllProblems] = useState([]);
  const jumpInputRef = useRef(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

  useEffect(() => {
    if (selectedCategory) {
      fetchCategoryProblems();
    }
  }, [selectedCategory]);

  const fetchCategoryProblems = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch all problems and user data in parallel
      const [problemsResponse, userResponse] = await Promise.all([
        api.get('/api/problems'),
        api.get('/api/auth/current-user')
      ]);

      // Get solved problem IDs
      const solvedProblemIds = userResponse.data.authenticated
        ? (userResponse.data.user.solvedProblems || [])
        : [];

      // Filter problems by selected category tag
      const filtered = problemsResponse.data.filter(p => p.tags && p.tags.includes(selectedCategory));

      if (filtered.length === 0) {
        setError(`No problems found for category: ${selectedCategory}`);
        setLoading(false);
        return;
      }

      // Filter to only show unsolved problems
      const unsolved = filtered.filter(p => !solvedProblemIds.includes(p.problemId));

      if (unsolved.length === 0) {
        setShowCongratsModal(true);
        setLoading(false);
        return;
      }

      setCategoryProblems(unsolved);

      // If targetProblemId is provided, try to find and load that specific problem
      let initialProblem = unsolved[0];
      let initialIndex = 0;

      if (targetProblemId) {
        const targetIndex = unsolved.findIndex(p => p.problemId === targetProblemId);
        if (targetIndex !== -1) {
          initialProblem = unsolved[targetIndex];
          initialIndex = targetIndex;
        }
      }

      const problemData = new Problem(initialProblem);
      setCurrentProblem(problemData);
      setCurrentProblemIndex(initialIndex);
      setSelectedTokens(problemData.blanks.map(() => []));
      setIsCorrect(null);
      setTestResults(null);
    } catch (err) {
      setError('Failed to load problem: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const addToken = (token) => {
    const newSelected = [...selectedTokens];
    const position = insertionPosition !== null ? insertionPosition : newSelected[activeBlankIndex].length;

    // Insert at specific position
    newSelected[activeBlankIndex] = [
      ...newSelected[activeBlankIndex].slice(0, position),
      token,
      ...newSelected[activeBlankIndex].slice(position)
    ];

    setSelectedTokens(newSelected);
    setIsCorrect(null);
    // Move insertion position forward after inserting
    setInsertionPosition(position + 1);
  };

  const removeToken = (blankIndex, tokenIndex) => {
    const newSelected = [...selectedTokens];
    newSelected[blankIndex] = newSelected[blankIndex].filter((_, i) => i !== tokenIndex);
    setSelectedTokens(newSelected);
    setIsCorrect(null);
    // Adjust insertion position if needed
    if (blankIndex === activeBlankIndex && insertionPosition !== null && insertionPosition > tokenIndex) {
      setInsertionPosition(Math.max(0, insertionPosition - 1));
    }
  };

  const deleteTokenBeforeCursor = () => {
    if (activeBlankIndex === null) return;

    const tokens = selectedTokens[activeBlankIndex];
    const position = insertionPosition !== null ? insertionPosition : tokens.length;

    if (position > 0) {
      removeToken(activeBlankIndex, position - 1);
      // Keep cursor at the same logical position (which is now one less)
      setInsertionPosition(position - 1);
    }
  };

  const clearBlank = () => {
    if (activeBlankIndex === null) return;

    const newTokens = [...selectedTokens];
    newTokens[activeBlankIndex] = [];
    setSelectedTokens(newTokens);
    setInsertionPosition(null);
  };

  const checkSolution = async () => {
    const result = currentProblem.checkSolution(selectedTokens);
    setTestResults(result);
    setIsCorrect(result.allPassed);

    // Scroll to test results after a brief delay to allow rendering
    setTimeout(() => {
      if (testResultsRef.current) {
        testResultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);

    // If all tests passed, mark problem as solved and show feedback
    if (result.allPassed) {
      try {
        await api.post('/api/problems/solved', {
          problemId: currentProblem.id
        });

        // Track this problem as solved in this session
        const newSolved = new Set(solvedInSession);
        newSolved.add(currentProblem.id);
        setSolvedInSession(newSolved);

        // Show feedback dialog
        setShowFeedback(true);
        setFeedbackSubmitted(false);

        // Scroll to feedback dialog after a delay to allow rendering
        setTimeout(() => {
          if (feedbackRef.current) {
            feedbackRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        }, 300);

        // Check if all problems are now solved
        const remainingUnsolved = categoryProblems.filter(p => !newSolved.has(p.problemId));
        if (remainingUnsolved.length === 0) {
          setShowCongratsModal(true);
        }
      } catch (error) {
        console.error('Failed to mark problem as solved:', error);
        // Don't show error to user - this is non-critical
      }
    }
  };

  const submitFeedback = async (feedbackType) => {
    try {
      await api.post('/api/feedback', {
        problemId: currentProblem.id,
        feedbackType
      });
      setFeedbackSubmitted(true);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      // Still show "Thank you" even if submission fails
      setFeedbackSubmitted(true);
    }
  };

  const reset = () => {
    setSelectedTokens(currentProblem.blanks.map(() => []));
    setIsCorrect(null);
    setTestResults(null);
    setActiveBlankIndex(0);
    setInsertionPosition(null);
  };

  const nextProblem = () => {
    // Get list of unsolved problems (excluding those solved in this session)
    const unsolvedProblems = categoryProblems.filter(p => !solvedInSession.has(p.problemId));

    if (unsolvedProblems.length === 0) {
      // All problems solved - show congratulations modal
      setShowCongratsModal(true);
      return;
    }

    // Find current problem index in the original list
    const currentIdx = categoryProblems.findIndex(p => p.problemId === currentProblem.id);

    // Look for next unsolved problem after current index
    let nextProblemData = null;
    for (let i = currentIdx + 1; i < categoryProblems.length; i++) {
      if (!solvedInSession.has(categoryProblems[i].problemId)) {
        nextProblemData = categoryProblems[i];
        break;
      }
    }

    // If no unsolved problem after current, wrap around to beginning
    if (!nextProblemData) {
      for (let i = 0; i <= currentIdx; i++) {
        if (!solvedInSession.has(categoryProblems[i].problemId)) {
          nextProblemData = categoryProblems[i];
          break;
        }
      }
    }

    if (!nextProblemData) {
      // Shouldn't happen, but just in case
      setShowCongratsModal(true);
      return;
    }

    const nextIndex = categoryProblems.findIndex(p => p.problemId === nextProblemData.problemId);
    setCurrentProblemIndex(nextIndex);

    const problemData = new Problem(nextProblemData);
    setCurrentProblem(problemData);
    setSelectedTokens(problemData.blanks.map(() => []));
    setIsCorrect(null);
    setTestResults(null);
    setActiveBlankIndex(0);
    setInsertionPosition(null);
    setShowFeedback(false);
    setFeedbackSubmitted(false);
  };

  // Fetch all problems for jump-to-problem feature
  const fetchAllProblems = async () => {
    try {
      const response = await api.get('/api/problems');
      setAllProblems(response.data);
    } catch (error) {
      console.error('Failed to fetch all problems:', error);
    }
  };

  // Open jump dialog and fetch all problems
  const openJumpDialog = () => {
    setShowJumpDialog(true);
    setJumpSearchQuery('');
    if (allProblems.length === 0) {
      fetchAllProblems();
    }
    // Focus input after dialog opens
    setTimeout(() => {
      if (jumpInputRef.current) {
        jumpInputRef.current.focus();
      }
    }, 100);
  };

  // Filter problems based on search query
  const getFilteredProblems = () => {
    if (!jumpSearchQuery.trim()) {
      return allProblems.slice(0, 10); // Show first 10 if no query
    }

    const query = jumpSearchQuery.toLowerCase();
    return allProblems.filter(p =>
      p.title.toLowerCase().includes(query) ||
      p.problemId.toLowerCase().includes(query) ||
      (p.tags && p.tags.some(tag => tag.toLowerCase().includes(query)))
    ).slice(0, 10); // Limit to 10 results
  };

  // Jump to selected problem
  const jumpToProblem = (problem) => {
    const problemData = new Problem(problem);
    setCurrentProblem(problemData);
    setSelectedTokens(problemData.blanks.map(() => []));
    setIsCorrect(null);
    setTestResults(null);
    setActiveBlankIndex(0);
    setInsertionPosition(null);
    setShowFeedback(false);
    setFeedbackSubmitted(false);

    // Update current index if problem is in current category
    const indexInCategory = categoryProblems.findIndex(p => p.problemId === problem.problemId);
    if (indexInCategory !== -1) {
      setCurrentProblemIndex(indexInCategory);
    }

    setShowJumpDialog(false);
    setJumpSearchQuery('');
  };

  const handleTitleClick = () => {
    console.log('Title clicked. User:', user, 'isAdmin:', user?.isAdmin);

    // Only allow admins to edit tags
    if (!user?.isAdmin) {
      console.log('Not admin, returning');
      return;
    }

    const now = Date.now();
    const DOUBLE_TAP_DELAY = 300; // milliseconds

    if (now - lastTapRef.current < DOUBLE_TAP_DELAY) {
      // Double tap detected - open tag editor
      console.log('Double tap detected!');
      setEditingTags(currentProblem.tags.join(', '));
      setShowTagEditor(true);
    } else {
      console.log('Single tap');
    }

    lastTapRef.current = now;
  };

  const handleSaveTags = async () => {
    try {
      // Parse comma-separated tags
      const newTags = editingTags
        .split(',')
        .map(tag => tag.trim().toLowerCase())
        .filter(tag => tag.length > 0);

      // Update tags via API
      await api.put(`/api/problems/${currentProblem.id}/tags`, { tags: newTags });

      // Update local state
      currentProblem.tags = newTags;

      // Update the problem in categoryProblems array
      const updatedProblems = categoryProblems.map(p =>
        p.problemId === currentProblem.id ? { ...p, tags: newTags } : p
      );
      setCategoryProblems(updatedProblems);

      setShowTagEditor(false);
    } catch (error) {
      console.error('Failed to update tags:', error);
      alert('Failed to update tags. Please try again.');
    }
  };

  const getTokenColor = (type) => {
    switch (type) {
      case 'keyword': return 'bg-purple-500 hover:bg-purple-600';
      case 'variable': return 'bg-blue-500 hover:bg-blue-600';
      case 'operator': return 'bg-green-500 hover:bg-green-600';
      case 'number': return 'bg-orange-500 hover:bg-orange-600';
      case 'property': return 'bg-cyan-500 hover:bg-cyan-600';
      case 'syntax': return 'bg-gray-500 hover:bg-gray-600';
      default: return 'bg-gray-500 hover:bg-gray-600';
    }
  };

  const renderCodeLine = (line, lineIndex) => {
    const blank = currentProblem.getBlankAtLine(lineIndex);

    if (blank) {
      const blankIndex = currentProblem.blanks.indexOf(blank);
      const tokens = selectedTokens[blankIndex];

      const leadingSpaces = line.match(/^\s*/)[0];

      const isActive = blankIndex === activeBlankIndex;
      const currentInsertPos = isActive ? insertionPosition : null;

      return (
        <div key={lineIndex} className="flex items-start gap-2">
          <span className="text-slate-500 select-none w-6 sm:w-8 text-right flex-shrink-0">{lineIndex + 1}</span>
          <div className="flex-1">
            <div
              onClick={() => {
                setActiveBlankIndex(blankIndex);
                setInsertionPosition(null);
              }}
              className={`min-h-[40px] bg-slate-900 rounded border-2 border-dashed p-2 flex flex-wrap items-center transition-all cursor-pointer ${
                isActive
                  ? 'border-yellow-500 shadow-lg shadow-yellow-500/30'
                  : 'border-slate-600 hover:border-yellow-400'
              }`}
              style={{ marginLeft: `${leadingSpaces.length * 0.5}em` }}
            >
              {tokens.length === 0 ? (
                <span className="text-slate-500 text-xs">&nbsp;</span>
              ) : (
                <div className="flex flex-wrap items-center gap-1 w-full">
                  {tokens.map((token, idx) => (
                    <React.Fragment key={idx}>
                      {/* Insertion point before token */}
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveBlankIndex(blankIndex);
                          setInsertionPosition(idx);
                        }}
                        className={`flex-shrink-0 h-7 cursor-pointer rounded transition-all ${
                          isActive && currentInsertPos === idx
                            ? 'w-1 bg-yellow-400 animate-pulse'
                            : 'w-1 hover:w-1.5 hover:bg-yellow-400/50'
                        }`}
                      />
                      {/* Token display (clickable - left/right positioning) */}
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          const rect = e.currentTarget.getBoundingClientRect();
                          const clickX = e.clientX - rect.left;
                          const halfWidth = rect.width / 2;

                          setActiveBlankIndex(blankIndex);
                          // If clicked on left half, position before token (idx)
                          // If clicked on right half, position after token (idx + 1 or null if last)
                          if (clickX < halfWidth) {
                            setInsertionPosition(idx);
                          } else {
                            setInsertionPosition(idx + 1 < tokens.length ? idx + 1 : null);
                          }
                        }}
                        className={`${getTokenColor(token.type)} text-white px-2 py-1 rounded text-xs font-semibold flex-shrink-0 cursor-pointer select-none active:opacity-100`}
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        {token.label}
                      </div>
                    </React.Fragment>
                  ))}
                  {/* Insertion point at the end - takes up remaining space */}
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveBlankIndex(blankIndex);
                      setInsertionPosition(null);
                    }}
                    className="flex-grow h-7 cursor-pointer rounded transition-all min-w-[16px] ml-1"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div key={lineIndex} className="flex items-start gap-2">
        <span className="text-slate-500 select-none w-6 sm:w-8 text-right flex-shrink-0">{lineIndex + 1}</span>
        <span className="text-slate-400">{line}</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 p-6 flex items-center justify-center">
        <div className="text-white text-2xl">Loading problem...</div>
      </div>
    );
  }

  if (showCongratsModal) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 p-6 flex items-center justify-center">
        <div className="bg-slate-800 rounded-lg p-8 border-2 border-yellow-400 max-w-md mx-auto text-center">
          <div className="text-6xl mb-4">🎉</div>
          <h2 className="text-3xl font-bold text-white mb-3">Congratulations!</h2>
          <p className="text-slate-300 text-lg mb-6">
            You've solved all problems in the <span className="text-yellow-400 font-semibold capitalize">{selectedCategory}</span> category!
          </p>
          <button
            onClick={() => onBackToMenu && onBackToMenu()}
            className="bg-yellow-400 hover:bg-yellow-500 text-slate-900 px-8 py-3 rounded-lg font-bold text-lg transition"
          >
            OK
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 p-6 flex items-center justify-center">
        <div className="text-red-400 text-xl">{error}</div>
      </div>
    );
  }

  if (!currentProblem) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 p-6">
      {/* Tag Editor Modal */}
      {showTagEditor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-lg p-6 border-2 border-yellow-400 max-w-md w-full">
            <h3 className="text-2xl font-bold text-white mb-4">Edit Problem Tags</h3>
            <p className="text-slate-300 text-sm mb-4">Enter tags separated by commas</p>
            <input
              type="text"
              value={editingTags}
              onChange={(e) => setEditingTags(e.target.value)}
              className="w-full bg-slate-900 text-white border border-slate-600 rounded px-4 py-2 mb-4 focus:outline-none focus:border-yellow-400"
              placeholder="e.g., arrays, easy, loops"
              autoFocus
            />
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowTagEditor(false)}
                className="bg-slate-600 hover:bg-slate-700 text-white px-6 py-2 rounded-lg font-semibold transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTags}
                className="bg-yellow-400 hover:bg-yellow-500 text-slate-900 px-6 py-2 rounded-lg font-bold transition"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto">
        <h1 className="text-5xl font-bold text-white mb-1 text-center">Kodala</h1>
        <h2 className="text-3xl font-bold text-slate-300 mb-2 text-center">Code Puzzle</h2>
        <p className="text-slate-400 text-center mb-8">Build the missing code pieces</p>

        {/* Problem Statement */}
        <div className="bg-slate-800 rounded-lg p-6 mb-6 border border-slate-700">
          <div className="flex items-start justify-between mb-2">
            <h2
              className={`text-2xl font-semibold text-white ${user?.isAdmin ? 'cursor-pointer select-none hover:text-yellow-400 transition-colors' : ''}`}
              onClick={handleTitleClick}
              style={{ WebkitTapHighlightColor: 'transparent' }}
              title={user?.isAdmin ? 'Double-tap to edit tags' : ''}
            >
              {currentProblem.title}
              {user?.isAdmin && <span className="text-xs ml-2 text-yellow-400">(admin)</span>}
            </h2>
            {currentProblem.tags && currentProblem.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {currentProblem.tags.map((tag, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 bg-slate-700 text-slate-300 rounded-full text-xs font-semibold capitalize"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
          <p className="text-slate-300 mb-4">{currentProblem.description}</p>
          <div className="space-y-2">
            <p className="text-sm text-slate-400 font-semibold">Test Cases:</p>
            {currentProblem.testCases.map((test, idx) => (
              <div key={idx} className="text-sm text-slate-300 font-mono bg-slate-900 p-2 rounded">
                Input: {JSON.stringify(test.input)} → Output: {JSON.stringify(test.output)}
              </div>
            ))}
          </div>
        </div>

        {/* Code Template */}
        <div className="bg-slate-800 rounded-lg p-6 mb-6 border border-slate-700">
          <div className="overflow-x-auto">
            <div className="font-mono text-xs sm:text-sm space-y-1 whitespace-pre min-w-max">
              {currentProblem.codeTemplate.map((line, idx) => renderCodeLine(line, idx))}
            </div>
          </div>
        </div>

        {/* Available Tokens */}
        <div className="bg-slate-800 rounded-lg p-6 mb-6 border border-slate-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Available Tokens</h3>
            <div className="flex gap-2">
              <button
                onClick={deleteTokenBeforeCursor}
                disabled={
                  selectedTokens[activeBlankIndex]?.length === 0 ||
                  (insertionPosition !== null ? insertionPosition : selectedTokens[activeBlankIndex]?.length) === 0
                }
                className="bg-red-600 hover:bg-red-700 disabled:bg-slate-700 disabled:cursor-not-allowed text-white px-4 py-2 rounded font-semibold transition flex items-center gap-2 text-sm"
                title="Delete token before cursor (Backspace)"
              >
                <XCircle size={16} />
                Delete
              </button>
              <button
                onClick={clearBlank}
                disabled={selectedTokens[activeBlankIndex]?.length === 0}
                className="bg-orange-600 hover:bg-orange-700 disabled:bg-slate-700 disabled:cursor-not-allowed text-white px-4 py-2 rounded font-semibold transition flex items-center gap-2 text-sm"
                title="Clear all tokens from active blank"
              >
                <RotateCcw size={16} />
                Clear
              </button>
            </div>
          </div>

          {/* Grouped Tokens */}
          <div className="space-y-4">
            {/* Keywords */}
            {currentProblem.availableTokens.some(t => t.type === 'keyword') && (
              <div>
                <h4 className="text-xs font-semibold text-purple-400 mb-2 uppercase tracking-wide">Keywords</h4>
                <div className="flex flex-wrap gap-2">
                  {currentProblem.availableTokens
                    .filter(t => t.type === 'keyword')
                    .map((token) => (
                      <button
                        key={token.id}
                        onClick={() => addToken(token)}
                        className={`${getTokenColor(token.type)} text-white px-3 py-2 rounded font-mono text-sm font-semibold transition transform hover:scale-105 active:scale-95`}
                      >
                        {token.label}
                      </button>
                    ))}
                </div>
              </div>
            )}

            {/* Variables */}
            {currentProblem.availableTokens.some(t => t.type === 'variable') && (
              <div>
                <h4 className="text-xs font-semibold text-blue-400 mb-2 uppercase tracking-wide">Variables</h4>
                <div className="flex flex-wrap gap-2">
                  {currentProblem.availableTokens
                    .filter(t => t.type === 'variable')
                    .map((token) => (
                      <button
                        key={token.id}
                        onClick={() => addToken(token)}
                        className={`${getTokenColor(token.type)} text-white px-3 py-2 rounded font-mono text-sm font-semibold transition transform hover:scale-105 active:scale-95`}
                      >
                        {token.label}
                      </button>
                    ))}
                </div>
              </div>
            )}

            {/* Operators */}
            {currentProblem.availableTokens.some(t => t.type === 'operator') && (
              <div>
                <h4 className="text-xs font-semibold text-green-400 mb-2 uppercase tracking-wide">Operators</h4>
                <div className="flex flex-wrap gap-2">
                  {currentProblem.availableTokens
                    .filter(t => t.type === 'operator')
                    .map((token) => (
                      <button
                        key={token.id}
                        onClick={() => addToken(token)}
                        className={`${getTokenColor(token.type)} text-white px-3 py-2 rounded font-mono text-sm font-semibold transition transform hover:scale-105 active:scale-95`}
                      >
                        {token.label}
                      </button>
                    ))}
                </div>
              </div>
            )}

            {/* Numbers */}
            {currentProblem.availableTokens.some(t => t.type === 'number') && (
              <div>
                <h4 className="text-xs font-semibold text-orange-400 mb-2 uppercase tracking-wide">Numbers</h4>
                <div className="flex flex-wrap gap-2">
                  {currentProblem.availableTokens
                    .filter(t => t.type === 'number')
                    .map((token) => (
                      <button
                        key={token.id}
                        onClick={() => addToken(token)}
                        className={`${getTokenColor(token.type)} text-white px-3 py-2 rounded font-mono text-sm font-semibold transition transform hover:scale-105 active:scale-95`}
                      >
                        {token.label}
                      </button>
                    ))}
                </div>
              </div>
            )}

            {/* Properties */}
            {currentProblem.availableTokens.some(t => t.type === 'property') && (
              <div>
                <h4 className="text-xs font-semibold text-cyan-400 mb-2 uppercase tracking-wide">Properties</h4>
                <div className="flex flex-wrap gap-2">
                  {currentProblem.availableTokens
                    .filter(t => t.type === 'property')
                    .map((token) => (
                      <button
                        key={token.id}
                        onClick={() => addToken(token)}
                        className={`${getTokenColor(token.type)} text-white px-3 py-2 rounded font-mono text-sm font-semibold transition transform hover:scale-105 active:scale-95`}
                      >
                        {token.label}
                      </button>
                    ))}
                </div>
              </div>
            )}

            {/* Syntax */}
            {currentProblem.availableTokens.some(t => t.type === 'syntax') && (
              <div>
                <h4 className="text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wide">Syntax</h4>
                <div className="flex flex-wrap gap-2">
                  {currentProblem.availableTokens
                    .filter(t => t.type === 'syntax')
                    .map((token) => (
                      <button
                        key={token.id}
                        onClick={() => addToken(token)}
                        className={`${getTokenColor(token.type)} text-white px-3 py-2 rounded font-mono text-sm font-semibold transition transform hover:scale-105 active:scale-95`}
                      >
                        {token.label}
                      </button>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-4 justify-center mb-6 flex-wrap">
          <button
            onClick={checkSolution}
            disabled={selectedTokens.some(tokens => tokens.length === 0)}
            className="bg-green-600 hover:bg-green-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-semibold transition flex items-center gap-2"
          >
            Check Solution
          </button>
          <button
            onClick={nextProblem}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition"
          >
            Next Problem {categoryProblems.length > 0 && `(${currentProblemIndex + 1}/${categoryProblems.length})`}
          </button>
          {user?.isAdmin && (
            <button
              onClick={openJumpDialog}
              className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-semibold transition flex items-center gap-2"
              title="Jump to any problem (Admin only)"
            >
              <Search size={18} />
              Jump to Problem
            </button>
          )}
        </div>

        {/* Result */}
        {isCorrect !== null && (
          <div ref={testResultsRef} className={`rounded-lg p-6 border-2 ${isCorrect ? 'bg-green-900 border-green-500' : 'bg-red-900 border-red-500'}`}>
            <div className="flex items-center gap-3 mb-4">
              {isCorrect ? (
                <>
                  <CheckCircle size={32} className="text-green-400" />
                  <div>
                    <h3 className="text-xl font-bold text-white">Correct! 🎉</h3>
                    <p className="text-green-200">All test cases passed!</p>
                  </div>
                </>
              ) : (
                <>
                  <XCircle size={32} className="text-red-400" />
                  <div>
                    <h3 className="text-xl font-bold text-white">Not quite right</h3>
                    <p className="text-red-200">Some test cases failed. Check the details below.</p>
                  </div>
                </>
              )}
            </div>

            {/* Test Results Details */}
            {testResults && testResults.results && (
              <div className="space-y-2 mt-4">
                <h4 className="text-sm font-semibold text-slate-300 mb-2">Test Cases:</h4>
                {testResults.results.map((result, idx) => (
                  <div
                    key={idx}
                    className={`p-3 rounded border ${
                      result.passed
                        ? 'bg-green-900/30 border-green-600'
                        : 'bg-red-900/30 border-red-600'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      {result.passed ? (
                        <CheckCircle size={16} className="text-green-400 mt-0.5 flex-shrink-0" />
                      ) : (
                        <XCircle size={16} className="text-red-400 mt-0.5 flex-shrink-0" />
                      )}
                      <div className="flex-1 text-sm font-mono">
                        <div className="text-slate-300">
                          <span className="text-slate-400">Input:</span>{' '}
                          {JSON.stringify(result.input)}
                        </div>
                        <div className="text-slate-300">
                          <span className="text-slate-400">Expected:</span>{' '}
                          {JSON.stringify(result.expected)}
                        </div>
                        {result.error ? (
                          <div className="text-red-300">
                            <span className="text-slate-400">Error:</span> {result.error}
                          </div>
                        ) : (
                          <div className={result.passed ? 'text-green-300' : 'text-red-300'}>
                            <span className="text-slate-400">Got:</span>{' '}
                            {JSON.stringify(result.result)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Feedback Dialog */}
        {showFeedback && isCorrect && (
          <div ref={feedbackRef} className="mt-6 bg-slate-800 rounded-lg p-6 border-2 border-yellow-400">
            {!feedbackSubmitted ? (
              <div className="text-center">
                <h3 className="text-xl font-bold text-white mb-4">Did you like this question?</h3>
                <div className="flex gap-4 justify-center">
                  <button
                    onClick={() => submitFeedback('liked')}
                    className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-lg font-semibold transition flex items-center gap-2"
                  >
                    <span className="text-2xl">👍</span>
                    Yes
                  </button>
                  <button
                    onClick={() => submitFeedback('disliked')}
                    className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded-lg font-semibold transition flex items-center gap-2"
                  >
                    <span className="text-2xl">👎</span>
                    No
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center">
                <h3 className="text-2xl font-bold text-white">Thank you!</h3>
                <p className="text-slate-300 mt-2">Your feedback helps us improve.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Jump to Problem Dialog */}
      {showJumpDialog && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShowJumpDialog(false)}>
          <div className="bg-slate-800 rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col border border-slate-700" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Search size={24} />
                Jump to Problem
              </h3>
              <button
                onClick={() => setShowJumpDialog(false)}
                className="text-slate-400 hover:text-white transition"
              >
                <XCircle size={24} />
              </button>
            </div>

            {/* Search Input */}
            <div className="mb-4">
              <input
                ref={jumpInputRef}
                type="text"
                value={jumpSearchQuery}
                onChange={(e) => setJumpSearchQuery(e.target.value)}
                placeholder="Search by title, ID, or tag..."
                className="w-full bg-slate-900 text-white px-4 py-3 rounded-lg border border-slate-600 focus:border-purple-500 focus:outline-none"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && getFilteredProblems().length > 0) {
                    jumpToProblem(getFilteredProblems()[0]);
                  } else if (e.key === 'Escape') {
                    setShowJumpDialog(false);
                  }
                }}
              />
              <p className="text-slate-400 text-sm mt-2">
                {jumpSearchQuery.trim() ? `Found ${getFilteredProblems().length} problem(s)` : `Showing ${Math.min(allProblems.length, 10)} of ${allProblems.length} problems`}
              </p>
            </div>

            {/* Problem List */}
            <div className="flex-1 overflow-y-auto space-y-2">
              {getFilteredProblems().length === 0 ? (
                <div className="text-center text-slate-400 py-8">
                  {allProblems.length === 0 ? 'Loading problems...' : 'No problems found'}
                </div>
              ) : (
                getFilteredProblems().map((problem) => (
                  <button
                    key={problem.problemId}
                    onClick={() => jumpToProblem(problem)}
                    className="w-full text-left bg-slate-900 hover:bg-slate-700 p-4 rounded-lg border border-slate-600 hover:border-purple-500 transition group"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h4 className="text-white font-semibold group-hover:text-purple-400 transition">
                          {problem.title}
                        </h4>
                        <p className="text-slate-400 text-sm mt-1">ID: {problem.problemId}</p>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {problem.tags && problem.tags.slice(0, 3).map((tag, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-1 bg-slate-700 text-slate-300 rounded text-xs font-semibold capitalize"
                          >
                            {tag}
                          </span>
                        ))}
                        {problem.tags && problem.tags.length > 3 && (
                          <span className="px-2 py-1 bg-slate-700 text-slate-300 rounded text-xs font-semibold">
                            +{problem.tags.length - 3}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>

            {/* Keyboard Shortcuts Help */}
            <div className="mt-4 pt-4 border-t border-slate-700">
              <p className="text-slate-400 text-xs">
                <span className="font-semibold">Tip:</span> Press <kbd className="px-2 py-1 bg-slate-900 rounded border border-slate-600 font-mono">Enter</kbd> to jump to first result, <kbd className="px-2 py-1 bg-slate-900 rounded border border-slate-600 font-mono">Esc</kbd> to close
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CodePuzzleGame;
